"use client"

import { useSearchParams } from "next/navigation"
import { useTranslations } from "next-intl"
import { useCallback, useEffect, useState } from "react"
import { Button } from "~/components/legacy/common/button"
import { Card } from "~/components/legacy/common/card"
import { H4 } from "~/components/legacy/common/heading"
import { Input } from "~/components/legacy/common/input"
import { Label } from "~/components/legacy/common/label"
import { GlobeIcon, SearchIcon, ShieldAlertIcon } from "~/components/ui/icons"
import { CheckerResults } from "./checker-results"
import type { CheckerResponse } from "./checker-types"

const examples = ["vercel.com", "nytimes.com", "github.com"]

function isApiErrorPayload(payload: unknown): payload is { error: string } {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "error" in payload &&
    typeof payload.error === "string"
  )
}

function isCheckerResponse(payload: unknown): payload is CheckerResponse {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "results" in payload &&
    Array.isArray(payload.results)
  )
}

export function AiCrawlerChecker() {
  const t = useTranslations("pages.ai_crawler_checker.checker")
  const searchParams = useSearchParams()
  const initialUrl = searchParams.get("url") ?? ""
  const [targetUrl, setTargetUrl] = useState(initialUrl)
  const [result, setResult] = useState<CheckerResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isPending, setPending] = useState(false)

  const runCheck = useCallback(
    async (url: string) => {
      const normalizedUrl = url.trim()

      if (!normalizedUrl) {
        setError(t("errors.empty_url"))
        setResult(null)
        return
      }

      setPending(true)
      setError(null)

      try {
        const response = await fetch(
          `/api/v1/robots-checker?url=${encodeURIComponent(normalizedUrl)}`,
        )
        const payload = (await response.json()) as unknown

        if (!response.ok || isApiErrorPayload(payload)) {
          throw new Error(isApiErrorPayload(payload) ? payload.error : t("errors.unable"))
        }

        if (!isCheckerResponse(payload)) {
          throw new Error(t("errors.unable"))
        }

        setResult(payload)
        const nextUrl = new URL(globalThis.location.href)
        nextUrl.searchParams.set("url", normalizedUrl)
        globalThis.history.replaceState(null, "", nextUrl)
      } catch (caughtError) {
        setResult(null)
        setError(caughtError instanceof Error ? caughtError.message : t("errors.unable"))
      } finally {
        setPending(false)
      }
    },
    [t],
  )

  useEffect(() => {
    if (initialUrl) {
      void runCheck(initialUrl)
    }
  }, [initialUrl, runCheck])

  return (
    <div className="flex flex-col gap-6">
      <form
        className="flex flex-col gap-3"
        onSubmit={event => {
          event.preventDefault()
          void runCheck(targetUrl)
        }}
      >
        <Label
          htmlFor="crawler-checker-url"
          className="font-mono text-xs uppercase tracking-widest text-muted-foreground"
        >
          {t("label")}
        </Label>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative min-w-0 flex-1">
            <GlobeIcon
              className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              id="crawler-checker-url"
              name="url"
              type="text"
              inputMode="url"
              autoComplete="url"
              spellCheck={false}
              placeholder={t("placeholder")}
              value={targetUrl}
              className="h-12 pl-10 font-mono text-sm"
              onChange={event => setTargetUrl(event.target.value)}
            />
          </div>
          <Button type="submit" isPending={isPending} prefix={<SearchIcon />} className="h-12">
            {t("submit")}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-xs text-muted-foreground">
          <span>{t("examples.label")}</span>
          {examples.map(example => (
            <button
              key={example}
              type="button"
              className="font-medium hover:text-primary"
              onClick={() => {
                setTargetUrl(example)
                void runCheck(example)
              }}
            >
              {example}
            </button>
          ))}
        </div>
      </form>

      {error && (
        <Card hover={false} className="border-destructive/30 bg-destructive/5">
          <div className="flex items-center gap-3">
            <ShieldAlertIcon className="size-5 text-destructive" aria-hidden />
            <H4 as="strong" className="text-base">
              {t("errors.title")}
            </H4>
          </div>
          <p className="text-sm text-muted-foreground">{error}</p>
        </Card>
      )}

      {result && <CheckerResults response={result} />}
    </div>
  )
}
